import { Home, Users, Package, Calendar, Settings, QrCode, ShoppingCart, Bell, Box, Clock, Printer, BarChart3, UserCheck, FileSpreadsheet, CreditCard, UserX, Shield, ArrowRightLeft, User, LifeBuoy, FileText } from 'lucide-react';
import { NavLink } from 'react-router-dom';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { useSidebar } from '@/contexts/SidebarContext';
import { usePlatformAdmin } from '@/hooks/use-platform-admin';
import { useAuth } from '@/hooks/use-auth';

const baseNavItems = [
  { to: '/', icon: Home, label: 'Ana Sayfa' },
  { to: '/visits', icon: UserCheck, label: 'Ziyaretler' },
  { to: '/customers', icon: Users, label: 'Müşteriler' },
  { to: '/packages', icon: Package, label: 'Paketler' },
  { to: '/products', icon: Box, label: 'Ürünler' },
  { to: '/bayi-trades', icon: ArrowRightLeft, label: 'Bayi Ticareti' },
  { to: '/neodeyecegim', icon: CreditCard, label: 'Ne Ödeyeceğim?' },
  { to: '/eirsaliye', icon: FileText, label: 'e-Irsaliye' },
  { to: '/sales', icon: ShoppingCart, label: 'Satışlar' },
  { to: '/collections', icon: CreditCard, label: 'Tahsilatlar' },
  { to: '/onhold-sale', icon: Clock, label: 'Beklemedeki Satışlar' },
  { to: '/stock-entry', icon: Package, label: 'Stok Girişi' },
  { to: '/inventory-snapshots', icon: FileSpreadsheet, label: 'Stok Snapshot' },
  { to: '/unmatched', icon: Users, label: 'Eşleşmemiş' },
  { to: '/unknown-customers', icon: UserX, label: 'Bilinmeyen Müşteriler' },
  { to: '/qr-print', icon: Printer, label: 'QR Etiket' },
  { to: '/reports', icon: BarChart3, label: 'Raporlar' },
  { to: '/calendar', icon: Calendar, label: 'Takvim' },
  { to: '/notifications', icon: Bell, label: 'Bildirimler' },
  { to: '/destek', icon: LifeBuoy, label: 'Destek' },
  { to: '/settings', icon: Settings, label: 'Ayarlar' },
];

export const DesktopSidebar = () => {
  const { isCollapsed } = useSidebar();
  const { isPlatformAdmin, loading: adminLoading } = usePlatformAdmin();
  const { userRole } = useAuth();
  const canSeeKnownCustomers = isPlatformAdmin || userRole === 'admin' || userRole === 'employee';
  const navItems = canSeeKnownCustomers
    ? [
        ...baseNavItems.slice(0, 14),
        { to: '/known-customers', icon: User, label: 'Tanınan Müşteriler' },
        ...baseNavItems.slice(14),
      ]
    : baseNavItems;

  return (
    <aside className={cn(
      "hidden md:flex min-h-screen glass-panel border-r border-white/20 flex-col transition-all duration-300",
      isCollapsed ? "w-16" : "w-64"
    )}>
      {/* Logo/Header */}
      <div className={cn("p-6", isCollapsed && "px-3")}>
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-accent flex items-center justify-center flex-shrink-0">
            <Package className="w-6 h-6 text-accent-foreground" />
          </div>
          {!isCollapsed && (
            <div className="overflow-hidden">
              <h1 className="text-h3 font-bold whitespace-nowrap">MüşteriX</h1>
              <p className="text-caption text-muted-foreground whitespace-nowrap">Mağaza Yönetimi</p>
            </div>
          )}
        </div>
      </div>

      {/* Navigation */}
      <nav className="flex-1 px-4 space-y-2">
        {navItems.map((item) => (
          <NavLink
            key={item.to}
            to={item.to}
            className={({ isActive }) =>
              cn(
                'flex items-center gap-3 px-4 py-3 rounded-xl transition-all duration-200 hover-lift relative group',
                isActive
                  ? 'bg-accent text-accent-foreground shadow-elevation-2'
                  : 'text-foreground/80 hover:text-foreground hover:bg-white/8',
                isCollapsed && 'justify-center px-2'
              )
            }
          >
            <item.icon className="w-5 h-5 flex-shrink-0" />
            {!isCollapsed && <span className="font-medium whitespace-nowrap overflow-hidden">{item.label}</span>}
            
            {/* Tooltip for collapsed state */}
            {isCollapsed && (
              <div className="absolute left-full ml-2 px-2 py-1 bg-popover text-popover-foreground text-sm rounded-md shadow-md opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-none whitespace-nowrap z-50">
                {item.label}
              </div>
            )}
          </NavLink>
        ))}
        
        {/* Platform Admin Link - Only visible to platform admins */}
        {!adminLoading && isPlatformAdmin && (
          <NavLink
            to="/admin"
            className={({ isActive }) =>
              cn(
                'flex items-center gap-3 px-4 py-3 rounded-xl transition-all duration-200 hover-lift relative group',
                'text-yellow-500 hover:text-yellow-400 hover:bg-yellow-500/10',
                isActive
                  ? 'bg-yellow-500/20 text-yellow-400 shadow-elevation-2'
                  : 'text-yellow-500/80',
                isCollapsed && 'justify-center px-2'
              )
            }
          >
            <Shield className="w-5 h-5 flex-shrink-0" />
            {!isCollapsed && <span className="font-medium whitespace-nowrap overflow-hidden">Platform Admin</span>}
            
            {/* Tooltip for collapsed state */}
            {isCollapsed && (
              <div className="absolute left-full ml-2 px-2 py-1 bg-popover text-popover-foreground text-sm rounded-md shadow-md opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-none whitespace-nowrap z-50">
                Platform Admin
              </div>
            )}
          </NavLink>
        )}
      </nav>

      {/* Bottom Actions */}
      <div className={cn("p-4 space-y-3", isCollapsed && "px-2")}>
        <Button 
          variant="outline" 
          className={cn(
            "w-full justify-start gap-3",
            isCollapsed && "justify-center px-2"
          )} 
          size={isCollapsed ? "sm" : "lg"} 
          asChild
        >
          <NavLink to="/qr-scan" className="relative group">
            <QrCode className="w-5 h-5 flex-shrink-0" />
            {!isCollapsed && <span className="whitespace-nowrap overflow-hidden">QR Tarama</span>}
            
            {/* Tooltip for collapsed state */}
            {isCollapsed && (
              <div className="absolute left-full ml-2 px-2 py-1 bg-popover text-popover-foreground text-sm rounded-md shadow-md opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-none whitespace-nowrap z-50">
                QR Tarama
              </div>
            )}
          </NavLink>
        </Button>
      </div>
    </aside>
  );
};